import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import confetti from "canvas-confetti";
import { useCart } from "@/lib/CartContext";
import { CheckCircle, ArrowRight, MapPin } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_IMG = "/assets/hero-logo.png";
const NEON_COLORS = ["#FF2C8C", "#7B2CFF", "#296CFF", "#FF7A00", "#F8F8FA", "#A742FF"];

function celebrate() {
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const defaults = { colors: NEON_COLORS, ticks: 260, gravity: 0.85, scalar: 1.2, disableForReducedMotion: true };
  confetti({ ...defaults, particleCount: 120, spread: 90, startVelocity: 55, origin: { y: 0.6 } });
  setTimeout(() => confetti({ ...defaults, particleCount: 60, angle: 60, spread: 65, origin: { x: 0, y: 0.75 } }), 180);
  setTimeout(() => confetti({ ...defaults, particleCount: 60, angle: 120, spread: 65, origin: { x: 1, y: 0.75 } }), 320);
  setTimeout(() => confetti({ ...defaults, particleCount: 80, spread: 110, startVelocity: 40, origin: { y: 0.55 } }), 600);
}

export default function Success() {
  const [status, setStatus] = useState({ state: "checking", message: "Confirming your order…" });
  const [orderId, setOrderId] = useState(null);
  const [celebrated, setCelebrated] = useState(false);
  const { clear } = useCart();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing session." });
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 10) {
        setStatus({ state: "timeout", message: "Still processing. Check your email shortly." });
        return;
      }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.order_id) setOrderId(res.data.order_id);
        if (res.data.payment_status === "paid") {
          setStatus({ state: "paid", message: "Payment confirmed. Welcome to House Rules!" });
          clear();
          return;
        }
        if (res.data.status === "expired") {
          setStatus({ state: "expired", message: "Session expired. Try again." });
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2500);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status.state === "paid" && !celebrated) {
      celebrate();
      setCelebrated(true);
    }
  }, [status.state, celebrated]);

  const ok = status.state === "paid";

  return (
    <div className="min-h-screen bg-[#080412] text-white font-sans" data-testid="success-page">
      <header className="border-b border-white/8 bg-[#080412]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="success-brand-logo">
            <img src={LOGO_IMG} alt="House Rules" className="h-12 md:h-14 w-auto rounded-lg drop-shadow-[0_0_20px_rgba(167,66,255,0.5)]" />
          </Link>
          <Link to="/" className="font-sora text-[10.5px] font-semibold uppercase tracking-[0.24em] flex items-center gap-2 text-white/80 hover:text-brandpink transition-colors" data-testid="success-back-link">
            Back to Site
          </Link>
        </div>
      </header>

      <main className="flex items-center justify-center p-6 min-h-[calc(100vh-5rem)]">
      <div className="relative max-w-xl w-full rounded-3xl hr-glass hr-neon-pink p-8 md:p-12 text-center overflow-hidden">
        {/* Ambient glow */}
        <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,44,140,0.4), transparent 70%)", filter: "blur(40px)" }} />
        <div aria-hidden className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(123,44,255,0.35), transparent 70%)", filter: "blur(40px)" }} />

        <div className="relative">
          {ok && (
            <div className="mx-auto mb-4 h-14 w-14 rounded-full hr-cta grid place-items-center">
              <CheckCircle size={28} weight="fill" className="text-white" />
            </div>
          )}

          <div
            className="font-display uppercase text-5xl md:text-6xl mb-3 leading-none tracking-[-0.01em]"
            data-testid="success-headline"
          >
            {ok ? (
              <>You&apos;re <span className="italic font-editorial text-brandpink hr-text-glow-pink">In.</span></>
            ) : (
              <>Hang <span className="italic font-editorial text-brandpink hr-text-glow-pink">Tight.</span></>
            )}
          </div>
          <p className="text-base md:text-lg text-white/75 mb-6" data-testid="success-message">{status.message}</p>
          {ok && (
            <p className="text-sm text-white/55 mb-6">
              We&apos;ll email your receipt and shipping confirmation. The box ships in 3–5 business days.
            </p>
          )}
          {orderId && (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 mb-6 text-left">
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-white/50 mb-1 flex items-center gap-2"><MapPin size={12} weight="fill" className="text-brandpink" /> Order ID</div>
              <div className="font-mono text-sm break-all text-white" data-testid="success-order-id">{orderId}</div>
              <p className="font-sans text-xs text-white/50 mt-2">
                Save this ID - use it to track your shipment any time.
              </p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 items-stretch justify-center">
            <Link
              to="/"
              className="hr-cta inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full font-sora font-semibold text-[11px] uppercase tracking-[0.24em] text-white"
              data-testid="back-home-btn"
            >
              Back to Site <ArrowRight size={14} />
            </Link>
            {orderId && (
              <Link
                to={`/orders/track?order_id=${encodeURIComponent(orderId)}`}
                className="hr-outline inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full font-sora font-semibold text-[11px] uppercase tracking-[0.24em] text-white"
                data-testid="success-track-link"
              >
                Track My Order <ArrowRight size={14} />
              </Link>
            )}
          </div>
        </div>
      </div>
      </main>
    </div>
  );
}
